import { notifications } from '@mantine/notifications';
import { UserUpdateMyCertificatesPayload } from 'api/actions/user-update-my-certificates/user-update-my-certificates-payload';
import { useApi } from 'api/api-context';
import useImmediateAction from 'api/use-immediate-action';
import MyCertificatesForm, { MyCertificatesFormData } from 'components/forms/my-certificates/MyCertificatesForm';
import { MY_CERTIFICATES_REDIRECT_AFTER_SAVE } from 'env';
import useOrganizationModules from 'features/organization-modules/use-organization-modules';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { DEVICE_TYPES } from 'model/DeviceType';
import { ROLE_ADMIN_ID } from 'model/Role';
import { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { DASHBOARD_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

/**
 * Page used to edit the currently logged in user's certificates.
 */
export default function MyCertificatesPage() {
  const { fullName, roleId, getAction } = useApi();
  const { hr } = useOrganizationModules();
  const navigate = useNavigate();

  const { data, loading, error } = useImmediateAction(() => {
    const userGetMyCertificates = getAction('UserGetMyCertificates');
    return userGetMyCertificates();
  });

  /**
   * Handles the form submission.
   */
  function submit({ certificates }: MyCertificatesFormData) {
    const updateMyCertificatesAction = getAction('UserUpdateMyCertificates');

    const mappedCertificates: UserUpdateMyCertificatesPayload = certificates.map(
      ({
        certificate,
        certificateId,
        number,
        renewalDate,
        type,
        stamp,
        paired,
        termVersion,
        nextDueDate,
        deadlineState,
      }) => ({
        certificateId,
        deviceTypeId: DEVICE_TYPES.find((deviceType) => deviceType.slug === type)?.id!,
        certificateNumber: number,
        renewalDate,
        stampFileId: stamp,
        certificateFileId: certificate,
        paired,
        termVersion,
        nextDueDate,
        deadlineState,
      })
    );

    updateMyCertificatesAction({ payload: mappedCertificates })
      .then(() => {
        notifications.show({
          title: 'Osvedčenia boli aktualizované',
          message: 'Vaše osvedčenia boli úspešne aktualizované.',
          color: SUCCESS_NOTIFICATION_COLOR,
        });

        if (MY_CERTIFICATES_REDIRECT_AFTER_SAVE === 'list') {
          navigate(DASHBOARD_PAGE_PATH.original);
        }
      })
      .catch(panic);
  }

  // Display the error if there is one.
  useEffect(() => {
    if (error) {
      panic(error);
    }
  }, [error]);

  if (roleId === ROLE_ADMIN_ID) {
    return <Navigate to={DASHBOARD_PAGE_PATH.original} replace />;
  }

  return (
    <DashboardLayout title="Moje osvedčenia" breadcrumbs={[{ title: fullName || '' }, { title: 'Moje osvedčenia' }]}>
      {!loading && !error && (
        <MyCertificatesForm
          onSubmit={submit}
          onRequalify={
            hr
              ? (certificate, values) => {
                  const requalify = getAction('UserRequalifyCertificate');
                  return requalify({
                    parameters: { certificateId: String(certificate.certificateId) },
                    payload: {
                      version: certificate.termVersion!,
                      ...values,
                    },
                  })
                    .then((result) => {
                      notifications.show({
                        title: 'Osvedčenie bolo rekvalifikované',
                        message: `Nový dátum obnovy je ${result.renewalDate}.`,
                        color: SUCCESS_NOTIFICATION_COLOR,
                      });
                      return result;
                    })
                    .catch((error) => {
                      panic(error);
                      throw error;
                    });
                }
              : undefined
          }
          initialValues={{
            certificates: (data || []).map((certificate) => ({
              type: certificate.deviceType!.slug,
              number: certificate.certificateNumber,
              renewalDate: certificate.renewalDate,
              stamp: certificate.stampFileId || '',
              certificate: certificate.certificateFileId || '',
              certificateId: certificate.certificateId,
              paired: certificate.paired,
              termVersion: certificate.termVersion,
              nextDueDate: certificate.nextDueDate,
              deadlineState: certificate.deadlineState,
            })),
          }}
        />
      )}
    </DashboardLayout>
  );
}
